import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Sparkles, Droplet, CheckCircle2, Truck, Camera, Star, Hash } from "lucide-react";

export default function QRScan() {
  const { pointId } = useParams();
  const [point, setPoint] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [requestCounts, setRequestCounts] = useState({}); // index -> antal (bestilling)
  const [action, setAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('request'); // 'request' eller 'complete'
  const [userRole, setUserRole] = useState(null);
  const [userIsLeader, setUserIsLeader] = useState(false);
  
  // Antal og sorteringskvalitet ved tømning
  const [emptyingDetails, setEmptyingDetails] = useState({});
  const [showEmptyingForm, setShowEmptyingForm] = useState(false);
  
  // Direkte tømningsregistrering (kørsel) uden bestillingsflow
  const [showDirectForm, setShowDirectForm] = useState(false);
  const [directDetails, setDirectDetails] = useState({});
  
  // Tjek om brugeren er logget ind
  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;

  useEffect(() => {
    loadData();
    
    // Hent brugerens rolle hvis logget ind
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUserRole(res.data.role);
        setUserIsLeader(!!res.data.is_leader);
      }).catch(() => {
        setUserRole(null);
      });
    }
  }, [pointId, token]);

  const isDriver = userRole === 'chauffør';
  const isAdmin = userRole === 'admin';
  // Hurtig tømningsvisning (bestil + registrér direkte): kørsel, admin, admin-assistent og ledere
  const useDriverView = isDriver || isAdmin || userRole === 'admin_assistant' || userRole === 'heart_leader' || userIsLeader;

  const loadData = async () => {
    try {
      const [pointRes, pendingRes] = await Promise.all([
        axios.get(`${API}/resource-points/${pointId}/public`),
        axios.get(`${API}/resource-points/${pointId}/pending-requests`)
      ]);
      setPoint(pointRes.data);
      setPendingRequests(pendingRes.data);
      
      // Hvis der er ventende anmodninger OG brugeren er logget ind, start i 'complete' mode
      if (pendingRes.data.length > 0 && isLoggedIn) {
        setMode('complete');
      }
    } catch (error) {
      toast.error("Kunne ikke finde ressourcepunkt");
    } finally {
      setLoading(false);
    }
  };

  // Find hvilke containere der allerede har ventende anmodninger
  const getPendingContainerKeys = () => {
    const pendingKeys = new Set();
    pendingRequests.forEach(req => {
      (req.containers || []).forEach(c => {
        pendingKeys.add(`${c.container_size}|${c.fraction_type}`);
      });
    });
    return pendingKeys;
  };

  const isContainerPending = (container) => {
    const pendingKeys = getPendingContainerKeys();
    return pendingKeys.has(`${container.container_size}|${container.fraction_type}`);
  };

  const handleContainerToggle = (index) => {
    setSelectedContainers(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Antal-tæller til bestillinger: tryk på række = +1, minus-knap = -1
  const incrementRequestCount = (index) => {
    setRequestCounts(prev => ({ ...prev, [index]: Math.min((prev[index] || 0) + 1, 10) }));
  };
  const decrementRequestCount = (index) => {
    setRequestCounts(prev => {
      const next = { ...prev };
      const n = (next[index] || 0) - 1;
      if (n <= 0) delete next[index]; else next[index] = n;
      return next;
    });
  };

  // Samlet bestilt antal for en container (fra ventende anmodninger)
  const getPendingCount = (container) => {
    let total = 0;
    pendingRequests.forEach(req => {
      (req.containers || []).forEach(c => {
        if (c.container_size === container.container_size && c.fraction_type === container.fraction_type) {
          total += c.count || 1;
        }
      });
    });
    return total;
  };

  // Chauffør bestiller tømning på ekstra containere
  const handleDriverRequestEmptying = async () => {
    const entries = Object.entries(requestCounts);
    if (entries.length === 0) {
      toast.error("Vælg mindst én container");
      return;
    }

    setSubmitting(true);
    try {
      const containersToRequest = entries.map(([idx, count]) => ({ ...point.containers[idx], count }));

      await axios.post(`${API}/emptying-requests`, {
        point_id: pointId,
        request_type: 'tømning',
        containers: containersToRequest,
        source: 'qr'
      });

      toast.success("Tømning bestilt!");
      setRequestCounts({});
      await loadData();
    } catch (error) {
      toast.error("Fejl ved bestilling");
    } finally {
      setSubmitting(false);
    }
  };

  // Kørsel: registrér tømning direkte uden bestillingsflow (logges til statistik)
  const handleDirectEmpty = async () => {
    const entries = Object.entries(requestCounts);
    if (!showDirectForm) {
      if (entries.length === 0) {
        toast.error("Vælg mindst én container");
        return;
      }
      const details = {};
      entries.forEach(([idx, count]) => {
        const container = point.containers[idx];
        details[idx] = {
          container_index: parseInt(idx),
          container_size: container.container_size,
          fraction_type: container.fraction_type,
          count,
          sorting_quality: 'good',
          notes: ''
        };
      });
      setDirectDetails(details);
      setShowDirectForm(true);
      return;
    }

    setSubmitting(true);
    try {
      const details = Object.values(directDetails);
      await axios.post(`${API}/resource-points/${pointId}/direct-empty`, {
        container_indices: details.map(d => d.container_index),
        container_details: details.map(d => ({
          container_index: d.container_index,
          count: d.count || 1,
          sorting_quality: d.sorting_quality || 'good',
          notes: d.notes || ''
        }))
      });

      toast.success("Tømning registreret!");
      setRequestCounts({});
      setDirectDetails({});
      setShowDirectForm(false);
      await loadData();
    } catch (error) {
      toast.error("Fejl ved registrering");
    } finally {
      setSubmitting(false);
    }
  };

  const updateDirectDetail = (key, field, value) => {
    setDirectDetails(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  // Bestil tømning (fra bod/bar-personale)
  const handleRequestEmptying = async () => {
    if (point.point_type === 'ressourceø' && Object.keys(requestCounts).length === 0) {
      toast.error("Vælg mindst én container");
      return;
    }

    if ((point.point_type === 'toilet' || point.point_type === 'pissoir') && !action) {
      toast.error("Vælg en handling");
      return;
    }

    setSubmitting(true);
    try {
      const containersToRequest = point.point_type === 'ressourceø' 
        ? Object.entries(requestCounts).map(([idx, count]) => ({ ...point.containers[idx], count }))
        : [];

      await axios.post(`${API}/emptying-requests`, {
        point_id: pointId,
        request_type: point.point_type === 'ressourceø' ? 'tømning' : action,
        containers: containersToRequest,
        source: 'qr'
      });

      toast.success("Tømning bestilt!");
      setRequestCounts({});
      await loadData();
    } catch (error) {
      toast.error("Fejl ved bestilling");
    } finally {
      setSubmitting(false);
    }
  };

  // Marker containere som tømt (fra chauffør)
  const handleCompleteContainers = async () => {
    if (selectedContainers.length === 0) {
      toast.error("Vælg mindst én container at markere som tømt");
      return;
    }

    // Vis formular til antal og kvalitet hvis ikke allerede udfyldt
    if (!showEmptyingForm) {
      // Initialiser emptyingDetails for valgte containere
      const details = {};
      selectedContainers.forEach(idx => {
        const container = point.containers[idx];
        const key = `${idx}`;
        details[key] = {
          container_index: idx,
          container_size: container.container_size,
          fraction_type: container.fraction_type,
          count: 1,
          sorting_quality: 'good', // good, medium, poor
          notes: ''
        };
      });
      setEmptyingDetails(details);
      setShowEmptyingForm(true);
      return;
    }

    setSubmitting(true);
    try {
      // Byg data med antal og kvalitet
      const containerData = Object.values(emptyingDetails).map(d => ({
        container_index: d.container_index,
        count: d.count || 1,
        sorting_quality: d.sorting_quality || 'good',
        notes: d.notes || ''
      }));

      const response = await axios.post(`${API}/resource-points/${pointId}/complete-containers`, {
        container_indices: selectedContainers,
        container_details: containerData
      });

      toast.success(`Containere markeret som tømt! Status: ${response.data.new_status}`);
      setSelectedContainers([]);
      setEmptyingDetails({});
      setShowEmptyingForm(false);
      await loadData();
    } catch (error) {
      toast.error("Fejl ved markering");
    } finally {
      setSubmitting(false);
    }
  };

  // Annuller tømningsformular
  const cancelEmptyingForm = () => {
    setShowEmptyingForm(false);
    setEmptyingDetails({});
  };

  // Opdater detaljer for en container
  const updateEmptyingDetail = (key, field, value) => {
    setEmptyingDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Henter data...</p>
      </div>
    );
  }

  if (!point) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-xl font-bold mb-2">Ressourcepunkt ikke fundet</p>
          <p className="text-muted-foreground">QR-koden er muligvis ugyldig</p>
        </div>
      </div>
    );
  }

  const pendingKeys = getPendingContainerKeys();
  const hasPendingRequests = pendingRequests.length > 0;

  return (
    <div className="min-h-screen bg-background p-4" data-testid="qr-scan-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              point.status === 'grøn' ? 'bg-green-500' :
              point.status === 'gul' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}>
              {point.point_type === 'ressourceø' ? (
                <Trash2 className="h-6 w-6 text-white" />
              ) : (
                <Sparkles className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold font-heading uppercase">{point.name}</h1>
              <p className="text-sm text-muted-foreground font-mono">
                {point.point_type.charAt(0).toUpperCase() + point.point_type.slice(1)}
              </p>
            </div>
          </div>

          {/* Status besked */}
          {point.status === 'gul' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-4 mb-6">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-yellow-500" />
                <p className="text-sm font-medium text-yellow-400">
                  🚛 Tømning er på vej
                </p>
              </div>
            </div>
          )}

          {/* Ressourceø - Container liste */}
          {point.point_type === 'ressourceø' && (
            <div>
              {/* For chauffører: Vis begge sektioner */}
              {useDriverView ? (
                <>
                  {/* Sektion 1: Containere der venter på tømning */}
                  {hasPendingRequests && (
                    <div className="mb-6">
                      <h2 className="text-lg font-bold font-heading uppercase mb-4 text-green-400">
                        ✓ Marker som tømt
                      </h2>
                      <div className="space-y-3">
                        {point.containers.filter(isContainerPending).map((container, idx) => {
                          const originalIndex = point.containers.findIndex(c => 
                            c.container_size === container.container_size && 
                            c.fraction_type === container.fraction_type
                          );
                          return (
                            <label
                              key={`complete-${originalIndex}`}
                              className={`flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30 cursor-pointer hover:bg-green-500/20 ${
                                selectedContainers.includes(originalIndex) ? 'ring-2 ring-green-500' : ''
                              }`}
                            >
                              <Checkbox
                                checked={selectedContainers.includes(originalIndex)}
                                onCheckedChange={() => handleContainerToggle(originalIndex)}
                                className="mt-1 border-green-500"
                                data-testid={`container-complete-${originalIndex}`}
                              />
                              <div className="flex-1">
                                <p className="font-bold font-mono">{container.container_size}</p>
                                <p className="text-sm text-muted-foreground">{container.fraction_type}</p>
                                <p className="text-xs text-orange-500 mt-1 font-medium">
                                  📦 Venter på tømning{getPendingCount(container) > 1 ? ` — ${getPendingCount(container)} stk` : ''}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      
                      {/* Tømningsformular - vises efter valg af containere */}
                      {showEmptyingForm ? (
                        <div className="mt-4 p-4 bg-zinc-800/50 border border-border rounded-lg space-y-4" data-testid="emptying-form">
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold text-green-400 flex items-center gap-2">
                              <Hash className="h-4 w-4" />
                              Udfyld tømningsdetaljer
                            </h3>
                            <Button variant="ghost" size="sm" onClick={cancelEmptyingForm}>
                              Annuller
                            </Button>
                          </div>
                          
                          {Object.entries(emptyingDetails).map(([key, detail]) => {
                            const container = point.containers[detail.container_index];
                            return (
                              <div key={key} className="p-3 bg-zinc-900/50 rounded-lg space-y-3">
                                <p className="font-medium text-sm">
                                  {container?.container_size} - {container?.fraction_type}
                                </p>
                                
                                {/* Antal containere */}
                                <div>
                                  <Label className="text-xs text-muted-foreground mb-1 block">
                                    Antal tømt
                                  </Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={detail.count}
                                    onChange={(e) => updateEmptyingDetail(key, 'count', parseInt(e.target.value) || 1)}
                                    className="h-9 w-24 bg-zinc-800"
                                    data-testid={`count-input-${key}`}
                                  />
                                </div>
                                
                                {/* Sorteringskvalitet */}
                                <div>
                                  <Label className="text-xs text-muted-foreground mb-2 block">
                                    Sorteringskvalitet
                                  </Label>
                                  <div className="flex gap-2 flex-wrap">
                                    {[
                                      { value: 'good', label: 'God', icon: '✓', color: 'bg-green-600' },
                                      { value: 'medium', label: 'OK', icon: '~', color: 'bg-yellow-600' },
                                      { value: 'poor', label: 'Dårlig', icon: '✗', color: 'bg-red-600' }
                                    ].map(opt => (
                                      <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => updateEmptyingDetail(key, 'sorting_quality', opt.value)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                                          detail.sorting_quality === opt.value
                                            ? `${opt.color} text-white ring-2 ring-white/30`
                                            : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                                        }`}
                                        data-testid={`quality-${opt.value}-${key}`}
                                      >
                                        <Star className="h-3 w-3" />
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          
                          <Button
                            data-testid="submit-emptying-btn"
                            onClick={handleCompleteContainers}
                            disabled={submitting}
                            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base uppercase font-bold"
                          >
                            {submitting ? "Gemmer..." : "✓ Bekræft tømning"}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          data-testid="complete-containers-button"
                          onClick={handleCompleteContainers}
                          disabled={submitting || selectedContainers.length === 0}
                          className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white h-12 text-base uppercase font-bold"
                        >
                          {submitting ? "Markerer..." : "✓ Marker valgte som tømt"}
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Sektion 2: Andre containere (bestil eller registrér direkte) */}
                  {point.containers.some(c => !isContainerPending(c)) && (
                    <div className={hasPendingRequests ? "pt-6 border-t border-border" : ""}>
                      <h2 className="text-lg font-bold font-heading uppercase mb-4 text-red-400">
                        🗑️ Andre containere
                      </h2>
                      <p className="text-sm text-muted-foreground mb-4">
                        Tryk på en container — tryk flere gange for at angive antal. Bestil tømning, eller registrér direkte som tømt hvis du tømmer nu.
                      </p>
                      <div className="space-y-3">
                        {point.containers.map((container, originalIndex) => {
                          if (isContainerPending(container)) return null;
                          const count = requestCounts[originalIndex] || 0;
                          return (
                            <div
                              key={`request-${originalIndex}`}
                              onClick={() => incrementRequestCount(originalIndex)}
                              className={`flex items-center gap-3 p-4 rounded-lg bg-secondary/50 cursor-pointer hover:bg-secondary select-none ${
                                count > 0 ? 'ring-2 ring-red-500' : ''
                              }`}
                              data-testid={`container-order-${originalIndex}`}
                            >
                              <div className="flex-1">
                                <p className="font-bold font-mono">{container.container_size}</p>
                                <p className="text-sm text-muted-foreground">{container.fraction_type}</p>
                              </div>
                              {count > 0 && (
                                <>
                                  <span className="text-xl font-bold text-red-400 font-mono" data-testid={`container-count-${originalIndex}`}>×{count}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); decrementRequestCount(originalIndex); }}
                                    className="w-9 h-9 rounded-full bg-zinc-700 hover:bg-zinc-600 text-white text-xl font-bold flex items-center justify-center"
                                    data-testid={`container-minus-${originalIndex}`}
                                  >−</button>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {showDirectForm ? (
                        <div className="mt-4 p-4 bg-zinc-800/50 border border-border rounded-lg space-y-4" data-testid="direct-empty-form">
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold text-emerald-400 flex items-center gap-2">
                              <Hash className="h-4 w-4" />
                              Registrér tømning
                            </h3>
                            <Button variant="ghost" size="sm" onClick={() => { setShowDirectForm(false); setDirectDetails({}); }}>
                              Annuller
                            </Button>
                          </div>
                          
                          {Object.entries(directDetails).map(([key, detail]) => (
                            <div key={key} className="p-3 bg-zinc-900/50 rounded-lg space-y-3">
                              <p className="font-medium text-sm">{detail.container_size} - {detail.fraction_type}</p>
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Antal tømt</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="20"
                                  value={detail.count}
                                  onChange={(e) => updateDirectDetail(key, 'count', parseInt(e.target.value) || 1)}
                                  className="h-9 w-24 bg-zinc-800"
                                  data-testid={`direct-count-input-${key}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground mb-2 block">Sorteringskvalitet</Label>
                                <div className="flex gap-2 flex-wrap">
                                  {[
                                    { value: 'good', label: 'God', color: 'bg-green-600' },
                                    { value: 'medium', label: 'OK', color: 'bg-yellow-600' },
                                    { value: 'poor', label: 'Dårlig', color: 'bg-red-600' }
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => updateDirectDetail(key, 'sorting_quality', opt.value)}
                                      className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                                        detail.sorting_quality === opt.value
                                          ? `${opt.color} text-white ring-2 ring-white/30`
                                          : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                                      }`}
                                      data-testid={`direct-quality-${opt.value}-${key}`}
                                    >
                                      <Star className="h-3 w-3" />
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            data-testid="direct-empty-confirm-btn"
                            onClick={handleDirectEmpty}
                            disabled={submitting}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base uppercase font-bold"
                          >
                            {submitting ? "Gemmer..." : "✓ Bekræft tømning"}
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-4 space-y-2">
                          <Button
                            data-testid="submit-request-button"
                            onClick={handleDriverRequestEmptying}
                            disabled={submitting || Object.keys(requestCounts).length === 0}
                            className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-base uppercase font-bold"
                          >
                            {submitting ? "Sender..." : "🗑️ Bestil tømning"}
                          </Button>
                          <Button
                            data-testid="direct-empty-button"
                            onClick={handleDirectEmpty}
                            disabled={submitting || Object.keys(requestCounts).length === 0}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base uppercase font-bold"
                          >
                            ✓ Registrér som tømt nu
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Hvis alt er OK */}
                  {!hasPendingRequests && point.containers.every(c => !isContainerPending(c)) && (
                    <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-center">
                      <p className="text-green-400 font-bold">✓ Ingen ventende tømninger</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Alle containere er OK. Du kan bestille tømning ovenfor hvis nødvendigt.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                /* Standard visning for admin og ikke-loggede */
                <>
                  <h2 className="text-lg font-bold font-heading uppercase mb-4">
                    {(mode === 'request' || !isLoggedIn) 
                      ? 'Vælg containere til tømning' 
                      : 'Marker containere som tømt'}
                  </h2>
                  
                  {(mode === 'request' || !isLoggedIn) && (
                    <p className="text-sm text-muted-foreground mb-4">
                      Tryk på en container for at bestille — tryk flere gange for at angive antal.
                    </p>
                  )}
                  
                  {point.containers && point.containers.length > 0 ? (
                    <div className="space-y-3">
                      {point.containers.map((container, index) => {
                        const isPending = isContainerPending(container);
                        const isRequestMode = mode === 'request' || !isLoggedIn;
                        const isDisabled = isRequestMode && isPending && !isAdmin;
                        
                        if (isRequestMode) {
                          const count = requestCounts[index] || 0;
                          return (
                            <div
                              key={index}
                              onClick={() => !isDisabled && incrementRequestCount(index)}
                              className={`flex items-center gap-3 p-4 rounded-lg transition-colors select-none ${
                                isDisabled 
                                  ? 'bg-secondary/30 opacity-60 cursor-not-allowed' 
                                  : 'bg-secondary/50 cursor-pointer hover:bg-secondary'
                              } ${count > 0 ? 'ring-2 ring-primary' : ''}`}
                              data-testid={`container-order-${index}`}
                            >
                              <div className="flex-1">
                                <p className="font-bold font-mono">{container.container_size}</p>
                                <p className="text-sm text-muted-foreground">{container.fraction_type}</p>
                                {isPending && (
                                  <p className="text-xs text-yellow-500 mt-1 font-medium">
                                    ⏳ Tømning allerede bestilt
                                  </p>
                                )}
                              </div>
                              {count > 0 && (
                                <>
                                  <span className="text-xl font-bold text-primary font-mono" data-testid={`container-count-${index}`}>×{count}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); decrementRequestCount(index); }}
                                    className="w-9 h-9 rounded-full bg-zinc-700 hover:bg-zinc-600 text-white text-xl font-bold flex items-center justify-center"
                                    data-testid={`container-minus-${index}`}
                                  >−</button>
                                </>
                              )}
                            </div>
                          );
                        }
                        
                        return (
                          <label
                            key={index}
                            className={`flex items-start gap-3 p-4 rounded-lg transition-colors bg-secondary/50 cursor-pointer hover:bg-secondary ${
                              selectedContainers.includes(index) ? 'ring-2 ring-primary' : ''
                            }`}
                          >
                            <Checkbox
                              checked={selectedContainers.includes(index)}
                              onCheckedChange={() => handleContainerToggle(index)}
                              className="mt-1"
                              data-testid={`container-checkbox-${index}`}
                            />
                            <div className="flex-1">
                              <p className="font-bold font-mono">{container.container_size}</p>
                              <p className="text-sm text-muted-foreground">{container.fraction_type}</p>
                              {isPending && (
                                <p className="text-xs text-orange-500 mt-1 font-medium">
                                  📦 Venter på tømning{getPendingCount(container) > 1 ? ` — ${getPendingCount(container)} stk` : ''}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Ingen containere på denne ø</p>
                  )}

                  {/* Info om ventende */}
                  {hasPendingRequests && mode === 'request' && !isAdmin && (
                    <div className="mt-4 p-3 bg-muted/50 rounded text-sm text-muted-foreground">
                      <p>💡 Containere markeret med "Tømning allerede bestilt" kan ikke vælges igen.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Toilet/Pissoir - Handlingsvalg (ikke for chauffører) */}
          {(point.point_type === 'toilet' || point.point_type === 'pissoir') && !isDriver && (
            <div>
              <h2 className="text-lg font-bold font-heading uppercase mb-4">
                Vælg handling
              </h2>
              
              <div className="space-y-3">
                <button
                  onClick={() => setAction('gør_ren')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    action === 'gør_ren'
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-secondary/50 hover:bg-secondary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-5 w-5" />
                    <div>
                      <p className="font-bold">Skal gøres rent</p>
                      <p className="text-sm text-muted-foreground">Bestil rengøring</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setAction('fyld_op')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    action === 'fyld_op'
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-secondary/50 hover:bg-secondary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Droplet className="h-5 w-5" />
                    <div>
                      <p className="font-bold">Skal fyldes op</p>
                      <p className="text-sm text-muted-foreground">Bestil genopfyldning</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Action button - kun for brugere UDEN den hurtige tømningsvisning (eller på toilet/pissoir) */}
          {!isDriver && !(useDriverView && point.point_type === 'ressourceø') && (
            isAdmin ? (
              // Admin: Baseret på mode
              (mode === 'request') ? (
                <Button
                  data-testid="submit-request-button"
                  onClick={handleRequestEmptying}
                  disabled={submitting}
                  className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white h-12 text-base uppercase font-bold"
                >
                  {submitting ? "Sender..." : "🗑️ Bestil tømning"}
                </Button>
              ) : showEmptyingForm ? (
                // Admin tømningsformular
                <div className="mt-6 p-4 bg-zinc-800/50 border border-border rounded-lg space-y-4" data-testid="admin-emptying-form">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-green-400 flex items-center gap-2">
                      <Hash className="h-4 w-4" />
                      Udfyld tømningsdetaljer
                    </h3>
                    <Button variant="ghost" size="sm" onClick={cancelEmptyingForm}>
                      Annuller
                    </Button>
                  </div>
                  
                  {Object.entries(emptyingDetails).map(([key, detail]) => {
                    const container = point.containers[detail.container_index];
                    return (
                      <div key={key} className="p-3 bg-zinc-900/50 rounded-lg space-y-3">
                        <p className="font-medium text-sm">
                          {container?.container_size} - {container?.fraction_type}
                        </p>
                        
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">Antal tømt</Label>
                          <Input
                            type="number"
                            min="1"
                            max="20"
                            value={detail.count}
                            onChange={(e) => updateEmptyingDetail(key, 'count', parseInt(e.target.value) || 1)}
                            className="h-9 w-24 bg-zinc-800"
                            data-testid={`admin-count-input-${key}`}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">Sorteringskvalitet</Label>
                          <div className="flex gap-2 flex-wrap">
                            {[
                              { value: 'good', label: 'God', color: 'bg-green-600' },
                              { value: 'medium', label: 'OK', color: 'bg-yellow-600' },
                              { value: 'poor', label: 'Dårlig', color: 'bg-red-600' }
                            ].map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => updateEmptyingDetail(key, 'sorting_quality', opt.value)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                                  detail.sorting_quality === opt.value
                                    ? `${opt.color} text-white ring-2 ring-white/30`
                                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                                }`}
                                data-testid={`admin-quality-${opt.value}-${key}`}
                              >
                                <Star className="h-3 w-3" />
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  <Button
                    data-testid="admin-submit-emptying-btn"
                    onClick={handleCompleteContainers}
                    disabled={submitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base uppercase font-bold"
                  >
                    {submitting ? "Gemmer..." : "✓ Bekræft tømning"}
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="complete-containers-button"
                  onClick={handleCompleteContainers}
                  disabled={submitting || selectedContainers.length === 0}
                  className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white h-12 text-base uppercase font-bold"
                >
                  {submitting ? "Markerer..." : "✓ Marker som tømt"}
                </Button>
              )
            ) : (
              // Ikke logget ind: Kun "Bestil tømning"
              <Button
                data-testid="submit-request-button"
                onClick={handleRequestEmptying}
                disabled={submitting}
                className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white h-12 text-base uppercase font-bold"
              >
                {submitting ? "Sender..." : "🗑️ Bestil tømning"}
              </Button>
            )
          )}

          {/* Beskrivelse */}
          {point.description && (
            <div className="mt-4 p-4 bg-muted/50 rounded">
              <p className="text-sm text-muted-foreground">{point.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
